import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/vendors")({
  component: VendorsPage,
});

import { useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import {
  useFetchVendors,
  useVendorMutations,
  type VendorOrdering,
} from "@/hooks/api/inventory/useCatalog";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useDebounce } from "@/hooks/use-debounce";
import { VendorTable } from "@/components/inventory/tables/VendorTable";
import { VendorFormSheet } from "@/components/inventory/forms/VendorFormSheet";
import type { Vendor, VendorPayload } from "@/types/inventory";

function VendorsPage() {
  const navigate = useNavigate();
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  // FILTERS + ORDERING
  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        name: SortDirection;
        is_active: SortDirection;
        created_at: SortDirection;
        updated_at: SortDirection;
      },
      VendorOrdering
    >({ name: null, is_active: null, created_at: null, updated_at: null });

  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(searchInput, 500);
  const [statusFilter, setStatusFilter] = useState<
    "true" | "false" | undefined
  >();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editing, setEditing] = useState<Vendor | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Vendor | null>(null);

  // QUERIES & MUTATIONS
  const vendorsQuery = useFetchVendors({
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
    is_active:
      statusFilter === "true"
        ? true
        : statusFilter === "false"
          ? false
          : undefined,
  });
  const vendors = vendorsQuery.data?.results ?? [];
  const count = vendorsQuery.data?.count ?? 0;

  const { createVendor, updateVendor, deleteVendor } = useVendorMutations();

  const isMutating =
    createVendor.isPending || updateVendor.isPending || deleteVendor.isPending;
  const isLoading = vendorsQuery.isPending || isMutating;
  const isFetching = vendorsQuery.isFetching || isMutating;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, statusFilter, setPage]);

  const resetFilters = () => {
    setOrdering({
      name: null,
      is_active: null,
      created_at: null,
      updated_at: null,
    });
    setSearchInput("");
    resetDebounce();
    setStatusFilter(undefined);
    setPage(1);
  };

  const handleSubmit = async (data: VendorPayload) => {
    if (editing) {
      await updateVendor.mutateAsync({ id: editing.id, payload: data });
      toast.success("Vendor updated");
    } else {
      await createVendor.mutateAsync(data);
      toast.success("Vendor created");
    }
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteVendor.mutateAsync({ id: deleteTarget.id });
      toast.success("Vendor deleted");
      setDeleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Vendors"
          description="Manage suppliers and vendors for purchase orders"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={() => {
            setEditing(null);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          addLabel="Add Vendor"
          isLoading={isFetching}
        />
        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            placeholder="Search vendors..."
            value={searchInput}
            onChange={setSearchInput}
          />
          <SelectFilter
            placeholder="Status"
            options={[
              { label: "Active", value: "true" },
              { label: "Inactive", value: "false" },
            ]}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
          />
        </FiltersPanel>
        <VendorTable
          vendors={vendors}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          onNameClick={(v) =>
            navigate({
              to: "/admin/inventory/vendors/$id",
              params: { id: String(v.id) },
            })
          }
          onView={(v) => {
            setEditing(v);
            setIsViewOnly(true);
            setIsSheetOpen(true);
          }}
          onEdit={(v) => {
            setEditing(v);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(v) => setDeleteTarget(v)}
        />
        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
        <VendorFormSheet
          isOpen={isSheetOpen}
          onClose={() => {
            setIsSheetOpen(false);
            setEditing(null);
          }}
          onSubmit={handleSubmit}
          editing={editing}
          isLoading={isMutating}
          isViewOnly={isViewOnly}
        />
        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Vendor"
          description={`Delete vendor "${deleteTarget?.name}"? This cannot be undone.`}
          itemName={deleteTarget?.name}
          isLoading={isMutating}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
